import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subject, switchMap, catchError } from 'rxjs';
import { WalletService } from '../services/wallet.service';
import { TransactionStore } from './transaction.store';
import { GozemMoneyAccountStore } from './gozem-money-account.store';
import type {
  WalletHomeAccountSection,
  WalletHomeBannersSection,
  WalletHomeSection,
  WalletHomeServicesSection,
  WalletHomeTransaction,
  WalletHomeTransactionSection,
  WalletHomeWalletAccountsSection,
  PaymentListItem,
} from '../models';
import type { TableColumn } from '@shared/types/table.types';
import { resolveServiceAction, type WalletServiceAction } from '@shared/utils/service-action.util';
import type { ServiceAmountLimits } from '@shared/utils/amount-validation.util';

/**
 * WalletStore
 *
 * Scoped store for the Wallet page — provided at WalletPage level via
 *   `providers: [WalletStore]`
 *
 * Single responsibility: fetch /api/merchant/gozem_money/home and expose
 * strongly-typed computed signals for every page section.
 *
 * Column management delegates to the global TransactionStore so columns are
 * reused from cache when the user visited the Transactions page first, and
 * fall back to a fresh getColumns() call otherwise — zero duplicate requests
 * in the common path.
 *
 * The load pipeline uses Subject + switchMap to cancel any in-flight request
 * when refresh() is called before the previous response arrives.
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable()
export class WalletStore {
  private readonly walletService = inject(WalletService);
  private readonly transactionStore = inject(TransactionStore);
  private readonly gmAccountStore = inject(GozemMoneyAccountStore);
  private readonly destroyRef = inject(DestroyRef);

  // ── Load pipeline ─────────────────────────────────────────────────────────
  private readonly loadRequest$ = new Subject<void>();

  // ── Private mutable state ─────────────────────────────────────────────────
  private readonly _loading = signal(true);
  private readonly _error = signal<string | null>(null);
  private readonly _sections = signal<WalletHomeSection[]>([]);

  // ── Public readonly state ─────────────────────────────────────────────────
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // ── Section selectors ─────────────────────────────────────────────────────

  readonly accountSection = computed(
    () => this._sections().find((s): s is WalletHomeAccountSection => s.type === 'account') ?? null,
  );

  readonly walletAccountsSection = computed(
    () =>
      this._sections().find(
        (s): s is WalletHomeWalletAccountsSection => s.type === 'wallet_accounts',
      ) ?? null,
  );

  readonly servicesSection = computed(
    () =>
      this._sections().find((s): s is WalletHomeServicesSection => s.type === 'services') ?? null,
  );

  readonly bannersSection = computed(
    () => this._sections().find((s): s is WalletHomeBannersSection => s.type === 'banners') ?? null,
  );

  readonly transactionSection = computed(
    () =>
      this._sections().find((s): s is WalletHomeTransactionSection => s.type === 'transaction') ??
      null,
  );

  // ── Derived account signals ───────────────────────────────────────────────

  /** Gozem Money wallet balance */
  readonly gozemMoneyBalance = computed(() => this.accountSection()?.gozem_money_balance ?? 0);

  /** Overdraft / pending total */
  readonly overdraftTotal = computed(() => this.accountSection()?.overdraft_total ?? 0);

  /** Available balance */
  readonly availableBalance = computed(() => this.accountSection()?.available_balance ?? 0);

  /** Base64 data URI QR code from the API — replaces QR Server external call */
  readonly qrCode = computed(() => this.accountSection()?.qr_code ?? '');

  /** Full payment URL from the API — e.g. "https://test.gozem.co/pay/49408908" */
  readonly paymentDeepLink = computed(
    () => this.accountSection()?.primary_account_deep_link_url ?? '',
  );

  // ── Derived wallet-accounts signals ──────────────────────────────────────

  /** Account number of the primary wallet account */
  readonly primaryAccountNumber = computed(
    () =>
      this.walletAccountsSection()?.accounts.find((a) => a.primary_account)?.account_number ?? '',
  );

  // ── Derived services signal ───────────────────────────────────────────────

  readonly services = computed(() => this.servicesSection()?.services ?? []);

  // ── Per-operation amount limits (GPGT-40760) ──────────────────────────────
  // Projects each flow's WalletService (min / max / multiple-of, from the home
  // payload) into the shape the flow modals validate against. A missing service
  // or null field ⇒ unbounded (backend validates on submission). The service is
  // resolved by `link` via the shared resolveServiceAction — the same mapping
  // WalletPage.onAction uses to route, so routing and limits cannot drift.

  readonly topupLimits = computed(() => this.limitsForAction('topup'));
  readonly sendLimits = computed(() => this.limitsForAction('send'));
  readonly withdrawLimits = computed(() => this.limitsForAction('withdraw'));

  private limitsForAction(action: WalletServiceAction): ServiceAmountLimits {
    const service = this.services().find((s) => resolveServiceAction(s.link) === action);
    return {
      min: service?.amount_min ?? null,
      max: service?.amount_max ?? null,
      multipleOf: service?.amount_multiple_of ?? null,
    };
  }

  // ── Derived banners signal ────────────────────────────────────────────────

  readonly banners = computed(() => this.bannersSection()?.content ?? []);

  // ── Derived transaction signals ───────────────────────────────────────────

  /** Raw wallet-home transactions (before PaymentListItem mapping) */
  readonly rawTransactions = computed(
    (): WalletHomeTransaction[] => this.transactionSection()?.transactions ?? [],
  );

  /** Section title — e.g. "Récentes transactions" */
  readonly transactionsTitle = computed(
    () => this.transactionSection()?.title ?? 'Récentes transactions',
  );

  /**
   * Transactions mapped to PaymentListItem for DataTableComponent.
   * Fields absent from the wallet-home payload are set to safe defaults.
   */
  readonly transactions = computed((): PaymentListItem[] => {
    const accountNumber = this.primaryAccountNumber();
    return this.rawTransactions().map((tx) => ({
      id: tx._id,
      type: tx.type,
      amount: tx.amount,
      statusCode: tx.status_code,
      description: tx.description,
      reference: tx.reference,
      accountNumber: accountNumber || '—',
      valueDate: tx.display_info.date,
      icon: tx.display_info.icon,
      title: tx.display_info.title,
      status: tx.display_info.status,
      formattedAmount: tx.display_info.amount,
      date: tx.display_info.date,
      balanceBefore: tx.display_info.balance_before,
      balanceAfter: tx.display_info.balance_after,
      createdAt: new Date(tx.created_at),
      orderingDate: new Date(tx.created_at),
      hasAttachments: false,
      attachmentCount: 0,
      counterpart: tx.display_info.title,
      sentVia: '',
      baseAmount: '',
      fees: '',
      commission: '',
      accountId: '',
      statusIcon: '',
      backgroundColor: '',
      textColor: '',
    }));
  });

  // ── Column bridge from TransactionStore ───────────────────────────────────

  /**
   * Visible TransactionColumns mapped to TableColumn<PaymentListItem>.
   * Consumed by RecentTransactionsComponent → DataTableComponent.
   * Uses TransactionStore cache; triggers getColumns() only if not yet loaded.
   */
  readonly tableColumns = computed((): TableColumn<PaymentListItem>[] =>
    this.transactionStore.visibleColumns().map((col) => ({
      field: col.key,
      header: col.label,
      sortable: col.sortable,
      template: COLUMN_TEMPLATE_MAP[col.key],
    })),
  );

  readonly columnsLoading = this.transactionStore.columnsLoading;

  constructor() {
    // Set up the cancellable load pipeline
    this.loadRequest$
      .pipe(
        switchMap(() => {
          this._loading.set(true);
          this._error.set(null);
          // Reuse cached columns when available — no-op if already loaded
          this.transactionStore.loadColumns();
          return this.walletService.getWalletHome().pipe(
            catchError((err: HttpErrorResponse) => {
              this._error.set(toUserFriendlyError(err));
              this._loading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response.success) {
          this._sections.set(response.sections);
          // Store primary GM wallet account ID in-memory (never localStorage — XSS risk)
          const walletSection = response.sections.find(
            (s): s is WalletHomeWalletAccountsSection => s.type === 'wallet_accounts',
          );
          const primaryGmAccountId =
            walletSection?.accounts.find((a) => a.primary_account)?.id ?? '';
          this.gmAccountStore.setPrimaryAccountId(primaryGmAccountId);
        } else {
          this._error.set('Impossible de charger les données du portefeuille.');
        }
        this._loading.set(false);
      });

    // Fire the initial load
    this.loadRequest$.next();
  }

  /** Cancels any in-flight request and re-fetches all wallet data. */
  refresh(): void {
    this.loadRequest$.next();
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * Maps TransactionColumn keys to DataTable template names.
 * Only keys that require a special cell renderer are listed;
 * unlisted keys render as plain text.
 */
const COLUMN_TEMPLATE_MAP: Record<string, TableColumn['template']> = {
  title: 'avatar-title',
  counterpart: 'avatar-title',
  description: 'description',
  formattedAmount: 'amount',
  amount: 'amount',
  statusCode: 'status_code',
  status: 'status_code',
  category: 'category',
  tags: 'tags',
  location: 'location',
  balanceBefore: 'balance_before',
  balanceAfter: 'balance_after',
};

/** Converts an HTTP error into a user-friendly French message. */
function toUserFriendlyError(err: HttpErrorResponse): string {
  if (err.status === 500) {
    return 'Une erreur serveur est survenue. Veuillez réessayer.';
  }
  if (err.status === 401 || err.status === 403) {
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  if (err.status === 0) {
    return 'Connexion impossible. Vérifiez votre connexion internet.';
  }
  return 'Impossible de charger les données du portefeuille.';
}
